import { Fragment, useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';

// Types reflecting the Vue model usage
export interface DiscountValidation {
	success?: boolean;
	discount_code_min_order_amount?: string; // e.g. '25,00' or '25.00'
	formatted_discount_amount?: string; // e.g. '€ 10'
	errors?: string[];
}

export interface DiscountBannerModel {
	plans: {
		package?: {
			discount_code_validation?: DiscountValidation;
		};
	}[];
}

export interface DiscountBannerProps {
	model: DiscountBannerModel;
	containerSelector?: string; // default '#discount-code-banner-container'
}

const BannerBlock = styled.div`
	width: 95%;
	position: relative;
`;

const DiscountCodeText = styled.div`
	color: #0f0f41;
	display: inline-block;
	width: 70%;
	font-size: 24px;
	font-style: normal;
	font-weight: 900;
	line-height: 32px;

	@media (max-width: 1023px) {
		font-size: 14px;
		font-weight: 800;
		line-height: 16px;
	}
`;

const CloseButtonWrap = styled.a`
	position: absolute;
	top: 50%;
	right: 0;
	transform: translateY(-50%);
	background: #ffffff;
	width: 40px;
	height: 40px;
	border-radius: 1000px;

	.close-button {
		position: absolute;
		top: 50%;
		right: 50%;
		transform: translate(50%, -50%);
	}
`;

const CloseIcon = styled.span`
	display: inline-block;
	width: 12px;
	height: 12px;
	background: currentColor;
	mask: url('/marketing-shop/images/close-black.svg') center/contain no-repeat;
	-webkit-mask: url('/marketing-shop/images/close-black.svg') center/contain no-repeat;
`;

export function DiscountBanner({
	model,
	containerSelector = '#discount-code-banner-container',
}: DiscountBannerProps) {
	const [closed, setClosed] = useState(false);
	const containerRef = useRef<HTMLElement | null>(null);

	const discountCodeValidation: DiscountValidation | undefined =
		model.plans[0]?.package?.discount_code_validation;

	const validationStatus = Boolean(
		discountCodeValidation?.success ?? discountCodeValidation?.discount_code_min_order_amount,
	);

	const infoText = useMemo(() => {
		if (!discountCodeValidation) return '';
		const discount = discountCodeValidation.formatted_discount_amount?.replace(/\s/g, '') ?? '';
		const minOrder = discountCodeValidation.discount_code_min_order_amount?.replace('.', ',') ?? '';
		return `Your discount code is applied. You receive ${discount} discount with an order above € ${minOrder}.`;
	}, [discountCodeValidation]);

	useEffect(() => {
		containerRef.current = document.querySelector(containerSelector);
	}, [containerSelector]);

	// Update container classes according to status
	useEffect(() => {
		const container = containerRef.current;
		if (!container || !discountCodeValidation) return;

		container.classList.add('discount-code-banner');

		if (validationStatus && !closed) {
			container.classList.add('success-discount');
			container.classList.remove('fail-discount');
		} else {
			container.classList.add('fail-discount');
			container.classList.remove('success-discount');
		}
	}, [discountCodeValidation, validationStatus, closed]);

	// Hide container when closed
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		container.style.display = closed ? 'none' : '';
	}, [closed]);

	const onClose = (e: MouseEvent) => {
		e.preventDefault();
		setClosed(true);
	};

	const container = containerRef.current;
	if (!container || !discountCodeValidation || closed) return null;

	return createPortal(
		<BannerBlock className="discount-banner-block">
			<DiscountCodeText className="discount-code-text">
				{validationStatus ? (
					infoText
				) : (
					<Fragment>
						{(discountCodeValidation.errors ?? []).map((err, idx) => (
							<span key={'error-' + String(idx)}>{err}</span>
						))}
					</Fragment>
				)}
			</DiscountCodeText>
			<CloseButtonWrap href="#" className="close-discount-code-banner" onClick={onClose}>
				<div className="close-button">
					<CloseIcon aria-hidden={true} />
				</div>
			</CloseButtonWrap>
		</BannerBlock>,
		container,
	);
}

export default DiscountBanner;
